import React, { useCallback, useMemo, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import DashboardLayout from '@fuww/library/src/Dashboard/DashboardLayout';
import DrawerContentItem
  from '@fuww/library/src/Dashboard/Navigation/DrawerContent';
import { CollapsibleList } from '@fuww/library/src/List';
import { useRouter } from 'next/router';
import Anchor from '@fuww/library/src/Anchor';
import KeyboardArrowDown from '@fuww/library/src/Icons/KeyboardArrowDown';
import KeyboardArrowRight from '@fuww/library/src/Icons/KeyboardArrowRight';
import FashionUnited from '@fuww/library/src/Icons/FashionUnited';
import { routesToReplace } from '../../../lib/routerState';
import UserProvider, { UserConsumer } from '../../UserProvider';
import UserTab from '../../Navigation/UserTab';
import getDashboardNavigation from '../../../data/getDashboardNavigation';
import Head from '../../Head';
import LinkAndAnchor from '../../LinkAndAnchor';

const dashboardLayoutProperties = {
  children: PropTypes.node,
  pageTitle: PropTypes.string,
  paddingDesktop: PropTypes.string,
  paddingMobile: PropTypes.string,
  mobileWidth: PropTypes.string,
};

type DashboardLayoutPropertyTypes = InferProps<
  typeof dashboardLayoutProperties
> & {
  paddingDesktop?: string;
  paddingMobile?: string;
  mobileWidth?: string;
};

interface SubItemType {
  route?: string;
  parameters?: { retoolPath?: string };
}

const Layout = ({
  children,
  pageTitle,
  paddingDesktop,
  paddingMobile,
  mobileWidth,
}: DashboardLayoutPropertyTypes) => {
  const [open, setOpen] = useState(false);
  const [
    expandedItems,
    setExpandedItems,
  ] = useState<Record<string, boolean>>({});

  const { query, route: currentRoute } = useRouter();
  const trimmedRoute = currentRoute ? currentRoute.replace(/^\//, '') : '';
  const currentRouteName = (routesToReplace as Record<string, string>)[
    trimmedRoute] || trimmedRoute;

  const handleCollapseClick = (name: string) => {
    setExpandedItems({ ...expandedItems, [name]: !expandedItems[name] });
  };

  const isMenuItemActive = useCallback(
    (route?: string, parameters?: { retoolPath?: string }) => {
      if (route === 'retool') {
        if (typeof query === 'object') {
          return query.retoolPath === parameters?.retoolPath;
        }
        return false;
      }

      const normalizedCurrentRoute = routesToReplace[
        currentRouteName as keyof typeof routesToReplace] || currentRouteName;
      return route === normalizedCurrentRoute;
    }, [currentRouteName, query],
  );

  const isParentItemActive = useMemo(
    () => (subItems: SubItemType[]) => subItems.some(
      (subItem) => isMenuItemActive(subItem.route, subItem.parameters),
    ), [isMenuItemActive],
  );

  return (
    <UserProvider>
      <Head
        title={pageTitle ?? 'FashionUnited'}
        description="FashionUnited Dashboard"
      />
      <DashboardLayout
        onToggleDrawer={() => setOpen(!open)}
        open={open}
        logo={(
          <LinkAndAnchor
            route="dashboard"
            passHref
          >
            <FashionUnited
              width="168"
              height="18"
              title="FashionUnited"
            />
          </LinkAndAnchor>
        )}
        navigationItems={(
          <UserConsumer>
            {(user) => {
              const userRoles = user.roles ?? {};
              return getDashboardNavigation(userRoles).map(({
                name, icon, route, parameters, href, target, subItems,
              }) => (subItems && subItems.length > 0
                ? (
                  <div key={name}>
                    <Anchor
                      key={name}
                      onClick={() => handleCollapseClick(name)}
                    >
                      <DrawerContentItem
                        Icon={icon}
                        name={name}
                        open={open}
                        Submenu={expandedItems[name]
                          ? KeyboardArrowDown
                          : KeyboardArrowRight}
                        isSelected={isParentItemActive(subItems)}
                      />
                    </Anchor>
                    <CollapsibleList in={expandedItems[name]}>
                      {subItems.map(({
                        name: menuName,
                        icon: SubmenuIcon,
                        route: MenuRoute,
                        parameters: menuParameters,
                        href: menuHref,
                        target: menuTarget,
                      }) => (
                        <LinkAndAnchor
                          key={menuName}
                          route={MenuRoute}
                          href={menuHref}
                          target={menuTarget}
                          params={menuParameters}
                          onClick={() => setOpen(false)}
                          hoverUnderline
                        >
                          <DrawerContentItem
                            Icon={SubmenuIcon}
                            name={menuName}
                            open={open}
                            isSubItem
                            isSelected={isMenuItemActive(
                              MenuRoute, menuParameters,
                            )}
                          />
                        </LinkAndAnchor>
                      ))}
                    </CollapsibleList>
                  </div>
                )
                : (
                  <LinkAndAnchor
                    key={name}
                    route={route}
                    href={href}
                    target={target}
                    params={parameters}
                    onClick={() => setOpen(false)}
                    hoverUnderline
                  >
                    <DrawerContentItem
                      Icon={icon}
                      name={name}
                      open={open}
                      isSelected={isMenuItemActive(route, parameters)}
                    />
                  </LinkAndAnchor>
                )));
            }}
          </UserConsumer>
        )}
        userTab={(
          <UserTab />
        )}
        paddingDesktop={paddingDesktop}
        paddingMobile={paddingMobile}
        mobileWidth={mobileWidth}
      >
        { children }
      </DashboardLayout>
    </UserProvider>
  );
};

Layout.propTypes = dashboardLayoutProperties;

Layout.defaultProps = {
  children: <div />,
};

export default Layout;
